<template>

  <ab-flow-base-cmp class="select-box-editor-cmp" :block="block" :transparent="true" :class="classesString" :style="stylesString">
    <q-select
        class="full-width"
        v-model="currentValue"
        :multiple="!!multiple"
        :label="label"
        :hint="hint"
        :options="optionsList"
        emit-value
        map-options
        :behavior="behavior"
        :rules="validationRules"
        lazy-rules
        hide-bottom-space
        v-bind="dynamicProps"
        :new-value-mode="allowAddValues"
        :use-chips="!!multiple"
        :use-input="useSearch"
        @filter="filterFn"
    >
      <template v-if="hasDialog" #append>
        <q-btn
          round
          flat
          icon="close"
          size="md"
          class="select-box-close-btn"
          v-close-popup
        />
      </template>
    </q-select>
  </ab-flow-base-cmp>

</template>

<script>

import {renderMixins} from "../renderMixins";
import AbFlowBaseCmp from "../Containers/Designer/AbFlowBaseCmp.vue";
import {formComponentsMixins} from "../Form/formComponentsMixins";
import validatableFormFieldMixin from './validatableFormFieldMixin';
import {formDefaultValueMixin} from "./formDefaultValueMixin";

export default {
  components: {AbFlowBaseCmp},
  mixins: [renderMixins, formComponentsMixins, validatableFormFieldMixin, formDefaultValueMixin],
  props: ['block'],
  data: () => ({
    optionsList: [],
  }),
  name: "SelectEditorCmp",
  created() {
    // Set options list
    this.optionsList = this.prepareOptions();

    // Watch for options changes
    this.$watch('options', () => {
      this.optionsList = this.prepareOptions()
    }, {deep: true})

  },
  computed: {
    /**
     * Label
     * @return {string}
     */
    label() {
      return this.interpretString(this.block.properties?.label);
    },

    /**
     * Hint
     * @return {string}
     */
    hint() {
      return this.interpretString(this.block.properties?.hint) || undefined;
    },

    /**
     * Multiple value
     * @return {string}
     */
    multiple() {
      return this.block.properties?.multiple || undefined
    },

    /**
     * Computed property that returns the position of extra options.
     * @return {string} The position of extra options. If the `manualOptionsState` property of the block is not defined, it returns 'none'.
     */
    extraOptionsPosition() {
      return this.block.properties?.manualOptionsState || 'none';
    },

    /**
     * Computed property that returns an array of extra options.
     * Each option is an object with `title` and `value` properties.
     * If the `extraOptionsPosition` is 'none', it returns an empty array.
     * If the `manualOptions` property of the block is not defined, it also returns an empty array.
     * @return {Array} An array of extra options.
     */
    extraOptions() {
      return (this.block.properties?.manualOptions?.items || [])
        .map((v) => ({title: v.label, value: v.value}));
    },

    /**
     * Options list
     * @return {*}
     */
    options() {
      // Main options
      const mainOptions = this.getValue(this.block?.properties?.options) || [];

      // Extra options position
      const extraOptsPos = this.extraOptionsPosition;

      // Options
      const opts = [];

      // If extra options should be before main options
      if (extraOptsPos === 'before' && this.extraOptions.length) {
        opts.push(...this.extraOptions);
      }

      // Main options
      opts.push(...mainOptions);

      // If extra options should be after main options
      if (extraOptsPos !== 'before' && this.extraOptions.length) {
        opts.push(...this.extraOptions);
      }

      // return options
      return opts?.map(v => ({label: v.title || v.label || v.name || v.value, value: v.value || v.id}))
    },

    /**
     * Behavior property of the block.
     * This computed property returns the behavior property of the block if it exists, otherwise it returns 'default'.
     * @return {string} The behavior property of the block or 'default' if it doesn't exist.
     */
    behavior() {
      return this.block.properties?.behavior || 'default';
    },

    /**
     * Allow add values
     * @return {(function(): *)|undefined}
     */
    allowAddValues() {
      return this.block.properties?.allowAddValues ? "add" : undefined
    },

    /**
     * Computed property to determine if the search functionality should be enabled.
     * @return {boolean} True if search is enabled, false otherwise.
     */
    useSearch() {
      return this.block.properties?.useSearch === 1;
    },

    /**
     * Determines if the component should display as a dialog.
     * @return {boolean} True if the component should display as a dialog, false otherwise.
     */
    hasDialog() {
      return this.$q.platform.is.mobile !== true && this.behavior !== 'dialog'
        ? false
        : this.behavior !== 'menu';
    },
  },
  methods: {
    /**
     * Prepare options
     * @return {{label, value}[]}
     */
    prepareOptions() {
      return this.options.map(v => ({
        label: v.label || v.name || v.title,
        value: v.value || v.id
      }))
    },

    /**
     * Filter function
     * @param val
     * @param update
     */
    filterFn(val, update) {
      if (val === '') {
        update(() => {
          this.optionsList = this.prepareOptions()
        })
        return
      }

      update(() => {
        const needle = val.toLowerCase()
        this.optionsList = this.prepareOptions().filter(v => v.label.toLowerCase().indexOf(needle) > -1)
      })
    },
  }
}

</script>

<style lang="scss">

.select-box-editor-cmp {
  .q-field {

    .q-field__append {
      color: inherit;
    }

    .q-field__control {
      color: var(--control-focus-color, transparent);

      .q-field__native, .q-field__label {
        color: var(--control-text-color) !important;
      }

      &:before {
        border-color: currentColor !important;
      }
    }

    .q-field__messages {
      color: var(--control-text-color);
    }

    &.q-field--error {
      .q-field__messages, .text-negative {
        color: var(--foreground-color-validation-error, #c10015) !important;
      }
    }

    &.q-field--error {
      .q-field__bottom {
      }
    }

    &:not(.q-field--focused) {
      .q-field__control {
        &:before {
          border-color: var(--control-unfocused-color, transparent) !important;
        }
      }
    }
  }

  .q-icon {
    color: inherit;
  }
}

body {
  .select-box-close-btn {
    display: none;
  }
  .q-select__dialog .select-box-close-btn {
    display: block;
  }
}

</style>
